import React from "react";
import { Box, Typography } from "@mui/material";
import FilterBar from "./FilterBar";
import SummaryBar from "./SummaryBar";
import TopicBucketBreakdown from "./TopicBucketBreakdown";
import TopicItemTable from "./TopicItemTable";
import { DEFAULT_TOPIC_ORDER, TOPIC_GROUPS, humanizeTopicKey } from "./topicOrder";

// Flip this to switch between the two layouts — flag is intentionally a
// plain local const so it's a one-line change either way:
//  - true:  Topic Bucket Breakdown rows are expandable — click a row to see
//           that topic's item table inline, right there. No separate list
//           of tables below.
//  - false: current/legacy layout — breakdown table stays flat, every
//           topic's item table is listed separately underneath it.
const EXPANDABLE_BREAKDOWN = true;

/**
 * Detail tab — item-level sentiment-scored tables for the currently loaded
 * run, replacing the old GenericTable-based output. Entirely prop-driven:
 * no fetching here, ConversationsV2 passes down whatever it already has in
 * state (discussionTopics / dynamicTopicOrder), the same object the old
 * table rendering used.
 *
 * Brand-only throughout — category-side data is not read or computed
 * anywhere in this view.
 */
export default function DetailView({ discussionTopics, dynamicTopicOrder, runLabel, dates, selectedDate, onDateChange }) {
  const topics = discussionTopics || {};
  const order = dynamicTopicOrder || [];
  const topicOrder = [
    ...DEFAULT_TOPIC_ORDER.filter((k) => topics[k]),
    ...order.filter((k) => topics[k]),
  ];

  // Derive summary + breakdown stats client-side from each topic's
  // final_bucket_scoring (written by the backend sentimentScoring helper) —
  // entity_score only, never overall_score (which folds in category).
  let maxScore = -Infinity;
  let minScore = Infinity;

  const breakdownRows = topicOrder.map((topicKey) => {
    const topicData = topics[topicKey];
    const score = topicData?.final_bucket_scoring?.entity_score ?? null;

    if (score !== null) {
      if (score > maxScore) maxScore = score;
      if (score < minScore) minScore = score;
    }

    const group = TOPIC_GROUPS[topicKey];

    return {
      topicKey,
      label: humanizeTopicKey(topicKey),
      score,
      // Combined rows (group present) have no entity.items of their own —
      // their expanded view is the two subRows below instead.
      entityData: group ? undefined : topicData?.entity,
      subRows: group
        ? group.map(({ key, label }) => ({ label, score: topics[key]?.final_bucket_scoring?.entity_score ?? null, entityData: topics[key]?.entity }))
        : undefined,
    };
  });

  // Net position for this run: a plain average of every scored topic's
  // brand score — sum / topic count, not weighted by item count.
  const scoredRows = breakdownRows.filter((r) => r.score !== null);
  const overall = scoredRows.length > 0 ? scoredRows.reduce((sum, r) => sum + r.score, 0) / scoredRows.length : null;

  // List every topic tied at the max/min score, not just one.
  const mostPositive = breakdownRows
    .filter((r) => r.score !== null && r.score === maxScore)
    .map((r) => ({ label: r.label, score: r.score }));
  const mostNegative = breakdownRows
    .filter((r) => r.score !== null && r.score === minScore)
    .map((r) => ({ label: r.label, score: r.score }));

  if (topicOrder.length === 0) {
    return <Typography sx={{ fontSize: 13, color: "#667085", py: 3 }}>No conversation data for this run yet.</Typography>;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <FilterBar runLabel={runLabel} dates={dates} selectedDate={selectedDate} onDateChange={onDateChange} />

      <SummaryBar overall={overall} mostPositive={mostPositive} mostNegative={mostNegative} />

      <TopicBucketBreakdown rows={breakdownRows} expandable={EXPANDABLE_BREAKDOWN} />

      {!EXPANDABLE_BREAKDOWN &&
        breakdownRows.flatMap((row) =>
          row.subRows
            ? row.subRows.map((sub) => (
                <TopicItemTable key={sub.label} topicLabel={sub.label} entityData={sub.entityData} />
              ))
            : [<TopicItemTable key={row.topicKey} topicLabel={row.label} entityData={row.entityData} />]
        )}
    </Box>
  );
}
